import { signal, computed } from '@angular/core';
import { OrganizationFeatures } from 'api/addons/models/organization-features';
import { Organization } from 'api/addons/models/organization';

export interface PermissionsState {
    organizationFeatures: Record<string, OrganizationFeatures>;
    currentOrganizationKey: string | null;
    isLoaded: boolean;
}

const initialState: PermissionsState = {
    organizationFeatures: {},
    currentOrganizationKey: null,
    isLoaded: false,
};

export const permissionsState = signal<PermissionsState>(initialState);

// Computed signals for easy access to current organization features
export const currentOrganizationFeatures = computed(() => {
    const state = permissionsState();
    if (!state.currentOrganizationKey) {
        return null;
    }
    return state.organizationFeatures[state.currentOrganizationKey] || null;
});

export const hasEcmr = computed(() => {
    const features = currentOrganizationFeatures();
    return features?.ecmr || false;
});

export const hasAdvancedShippingNotice = computed(() => {
    const features = currentOrganizationFeatures();
    return features?.advancedShippingNotice || false;
});

export const hasConsignorPdf = computed(() => {
    const features = currentOrganizationFeatures();
    return features?.consignorCompletePdf || false;
});

export const hasConsigneePdf = computed(() => {
    const features = currentOrganizationFeatures();
    return features?.consigneeCompletePdf || false;
});

export const hasConsigneeArticlesRead = computed(() => {
    const features = currentOrganizationFeatures();
    return features?.consigneeArticlesRead || false;
});

export const hasConsigneeArticlesEditing = computed(() => {
    const features = currentOrganizationFeatures();
    return features?.consigneeArticlesEditing || false;
});

export const hasConsigneeArticleDiscrepancies = computed(() => {
    const features = currentOrganizationFeatures();
    return features?.consigneeArticleDiscrepancies || false;
});

export const hasExtendedQSInspection = computed(() => {
    const features = currentOrganizationFeatures();
    return features?.extendedQSInspection || false;
});

export const hasConsigneeShowItemQRCode = computed(() => {
    const features = currentOrganizationFeatures();
    return features?.consigneeShowItemQRCode || false;
});

export const hasCsvReporting = computed(() => {
    const features = currentOrganizationFeatures();
    return features?.csvReporting || false;
});

export const hasMonthlyDeliveryNoteReport = computed(() => {
    const features = currentOrganizationFeatures();
    return features?.monthlyDeliveryNoteReport || false;
});

export const hasBundleGrouping = computed(() => {
    const features = currentOrganizationFeatures();
    return features?.bundleGrouping || false;
});

export const hasGeneralGUIFeatures = computed(() => {
    const features = currentOrganizationFeatures();
    return features?.generalGUIFeatures || false;
});

export const hasAldiInfoTexts = computed(() => {
    const features = currentOrganizationFeatures();
    return features?.aldiInfoTexts || false;
});

export const hasAdminTestArea = computed(() => {
    const features = currentOrganizationFeatures();
    return features?.adminTestArea || false;
});

// Actions to update the permissions state
export const setOrganizationFeatures = (organizations: Organization[]) => {
    const featuresMap: Record<string, OrganizationFeatures> = {};

    organizations.forEach(org => {
        featuresMap[org._key] = org.features;
    });

    permissionsState.update(state => ({
        ...state,
        organizationFeatures: featuresMap,
        isLoaded: true,
    }));
};

export const setCurrentOrganization = (organizationKey: string) => {
    permissionsState.update(state => ({
        ...state,
        currentOrganizationKey: organizationKey,
    }));
};

export const updateOrganizationFeatures = (
    organizationKey: string,
    features: OrganizationFeatures
) => {
    permissionsState.update(state => ({
        ...state,
        organizationFeatures: {
            ...state.organizationFeatures,
            [organizationKey]: features,
        },
    }));
};

export const resetPermissions = () => {
    permissionsState.set(initialState);
};

// Helper function to check if features have changed
export const haveFeaturesChanged = (
    newOrganizations: Organization[]
): boolean => {
    const currentState = permissionsState();

    if (!currentState.isLoaded) {
        return true;
    }

    for (const org of newOrganizations) {
        const currentFeatures = currentState.organizationFeatures[org._key];
        if (!currentFeatures) {
            return true;
        }

        // Deep comparison of features
        const featureKeys = new Set<string>(Object.keys(currentFeatures));
        Object.keys(org.features).forEach(key => {
            featureKeys.add(key);
        });
        if (
            featureKeys
                .values()
                .some(
                    featureKey =>
                        currentFeatures[featureKey] !== org.features[featureKey]
                )
        ) {
            return true;
        }
    }

    return false;
};
